import React from 'react';
import { ArrowRight, ShieldCheck, Target, Sparkles, Users, Eye, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Eyebrow, SectionTitle, GridBg, GlowOrb } from '../components/SectionHeader';
import { values, pastPerformance } from '../mock';

const iconMap = { ShieldCheck, Target, Sparkles, Users };

const About = ({ onContact }) => {
  return (
    <div className="bg-[#0a0e1a]">
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <GridBg />
        <GlowOrb className="w-[600px] h-[600px] -top-32 -left-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-300 text-xs font-medium">
            <ShieldCheck className="w-3.5 h-3.5" /> About Us
          </span>
          <h1 className="mt-6 text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight max-w-4xl">
            Securing the Future of <span className="text-cyan-400">Compliance</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-3xl leading-relaxed">
            IronVision was founded to reduce the complexity of multi-framework compliance for federal and regulated organizations, enabling clients to focus on their core mission while maintaining a strong security and compliance posture.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Eyebrow>Our Mission</Eyebrow>
            <SectionTitle>Simplifying Compliance for <span className="text-cyan-400">Critical Industries</span></SectionTitle>
            <p className="mt-6 text-slate-300 leading-relaxed">We believe that compliance should be an enabler, not a burden. Our platform transforms how organizations approach multi-framework compliance by leveraging intelligent automation and pre-built crosswalk mappings.</p>
            <p className="mt-4 text-slate-400 leading-relaxed">By eliminating duplicate effort and providing unified visibility, we help CISOs, compliance officers, and risk managers achieve continuous readiness while reducing costs and complexity.</p>
          </div>
          <div className="grid gap-5">
            <div className="rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-slate-900/60 to-slate-950/30 p-7">
              <div className="flex items-center gap-3">
                <Eye className="w-6 h-6 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Our Vision</h3>
              </div>
              <p className="mt-4 text-slate-300 leading-relaxed">A world where compliance is continuous, automated, and invisible—allowing organizations to focus on innovation and growth.</p>
            </div>
            <div className="rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-slate-900/60 to-slate-950/30 p-7">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-cyan-400" />
                <h3 className="text-xl font-bold text-white">Our Promise</h3>
              </div>
              <p className="mt-4 text-slate-300 leading-relaxed">We practice what we preach. IronVision is <span className="text-cyan-300 font-semibold">CMMC Level 2 certified</span> and holds a <span className="text-cyan-300 font-semibold">Top Secret facility clearance</span>—delivering enterprise-grade security to the most sensitive federal missions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Our Values</Eyebrow>
            <SectionTitle>What Drives <span className="text-cyan-400">Us</span></SectionTitle>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map(v => {
              const Icon = iconMap[v.icon];
              return (
                <div key={v.title} className="rounded-2xl border border-cyan-400/10 bg-slate-900/40 p-7 hover:border-cyan-400/40 hover:-translate-y-1 transition duration-300">
                  <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                    {Icon && <Icon className="w-6 h-6 text-cyan-400" />}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">{v.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PAST PERFORMANCE - TIMELINE */}
      <section className="py-28 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <Eyebrow>Our Journey</Eyebrow>
            <SectionTitle>Past <span className="text-cyan-400">Performance</span></SectionTitle>
          </div>
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/40 via-cyan-400/20 to-transparent" />
            {pastPerformance.map((p, i) => (
              <div key={p.title} className={`relative mb-12 md:grid md:grid-cols-2 md:gap-12 ${i % 2 === 1 ? 'md:[direction:rtl]' : ''}`}>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-cyan-400 shadow-[0_0_16px_rgba(34,211,238,0.7)]" />
                <div className="pl-12 md:pl-0 md:pr-12 md:text-right [direction:ltr]">
                  <div className="rounded-xl border border-cyan-400/15 bg-slate-900/40 p-6">
                    <h3 className="text-lg font-bold text-white">{p.title}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed">{p.description}</p>
                  </div>
                </div>
                <div />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 relative overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] top-0 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">Join Us on Our <span className="text-cyan-400">Mission</span></h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto">Whether you're looking to transform your compliance operations or join our team, we'd love to hear from you.</p>
          <Button onClick={onContact} className="mt-10 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-8 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
            Contact Us <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
